import { useCallback, useContext, useEffect, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'
import { useSessionEntity } from '@context/EntityContext'
import { useIBCampaignsResultsWriteContext } from '@context/IBCampaignsResultsContext'

import { Title1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { LogoIcon } from '@ui/icons/LogoIcon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillPartnerType } from '@utils/companyName.utils'

import { AppMarketQRCode } from '@global/appMarketLink/desktop/AppMarketQRCode'
import { AppMarketLink } from '@global/appMarketLink/mobile/AppMarketLink'

import { useLogout } from '@hooks/useLogout'

import { Loading } from '@shared/components/Loading/Loading'

import styles from './LogoutPage.module.scss'

export const LogoutPage = (): JSX.Element => {
  const navigate = useNavigate()
  const entity = useSessionEntity()
  const { t } = useTranslation()

  const [isLoggingOut, setIsLoggingOut] = useState(true)
  const hasLoggedOutRef = useRef(false)

  const apiClient = useApiClient(ClientApiClient)
  const { logout } = useLogout()
  const [auth] = useContext(AuthSessionContext)

  const { refreshIBCampaigns } = useIBCampaignsResultsWriteContext()

  const performLogout = useCallback(async () => {
    if (!auth) {
      return setIsLoggingOut(false)
    }
    try {
      await apiClient.logOut(auth.id)
      setIsLoggingOut(false)
      refreshIBCampaigns()
      logout()
    } catch (error: unknown) {
      console.error('Failed to logout:', error)
    }
  }, [apiClient, auth, logout, refreshIBCampaigns])

  useEffect(() => {
    if (hasLoggedOutRef.current) {
      return
    }
    hasLoggedOutRef.current = true
    performLogout()
  }, [auth, performLogout])

  return (
    <Loading isLoading={isLoggingOut} showLoadingIcon text={t('Logging out...')}>
      <div className={styles.topWrapper}>
        <div className={styles.wrapper}>
          <div className={styles.qrCode}>
            <AppMarketQRCode />
          </div>
          <div className={styles.inner}>
            <LogoIcon
              size={80}
              color='text-primary-med-em'
              secondaryColor='text-secondary-med-em'
            />
            <Title1 className={styles.title} medium>
              {t('You have been successfully logged out')}
            </Title1>
            <Button.Secondary size='lg' onClick={() => navigate('/login')}>
              {t('Login')}
            </Button.Secondary>
            <br />
            {!isTickmillPartnerType(entity) && (
              <div className={styles.signUpBox}>
                <span>{t('Not a member')}</span>
                <Link to='/sign-up'>{t('Sign up.Sign up')}</Link>
              </div>
            )}
          </div>
          <AppMarketLink />
        </div>
      </div>
    </Loading>
  )
}
